import { z } from 'zod';
import { AdfDocumentSchema } from './adf.js';
/**
 * Схемы ответов Jira Cloud REST API v3 и OAuth-эндпоинтов Atlassian.
 * Каждый ответ проверяется этими схемами на входе — типизация без `any` и без `as`.
 *
 * Объекты объявлены как loose: Atlassian добавляет поля без предупреждения, и незнакомый ключ
 * не должен ронять запрос. Обязательным считается только то, на что мы реально опираемся.
 */
// ── OAuth ────────────────────────────────────────────────────────────────────
export const JiraTokenResponseSchema = z.looseObject({
    access_token: z.string().min(1),
    /** Приходит только при scope offline_access. Ротируемый: старый гасится сразу. */
    refresh_token: z.string().min(1).optional(),
    expires_in: z.number().int().positive(),
    scope: z.string().optional(),
    token_type: z.string().optional(),
});
export const AccessibleResourceSchema = z.looseObject({
    /** cloudId сайта — подставляется в /ex/jira/{cloudId}/... */
    id: z.string().min(1),
    url: z.string(),
    name: z.string(),
    scopes: z.array(z.string()).optional(),
    avatarUrl: z.string().optional(),
});
export const AccessibleResourcesSchema = z.array(AccessibleResourceSchema);
// ── Общие сущности ───────────────────────────────────────────────────────────
export const JiraAvatarUrlsSchema = z.looseObject({
    '16x16': z.string().optional(),
    '24x24': z.string().optional(),
    '32x32': z.string().optional(),
    '48x48': z.string().optional(),
});
export const JiraUserSchema = z.looseObject({
    /** Исполнитель всегда задаётся accountId, имя для этого не годится. */
    accountId: z.string(),
    accountType: z.string().optional(),
    displayName: z.string().optional(),
    emailAddress: z.string().optional(),
    active: z.boolean().optional(),
    avatarUrls: JiraAvatarUrlsSchema.optional(),
    timeZone: z.string().optional(),
    locale: z.string().optional(),
});
export const JiraStatusCategorySchema = z.looseObject({
    id: z.number().optional(),
    key: z.string(),
    name: z.string().optional(),
    colorName: z.string().optional(),
});
export const JiraStatusSchema = z.looseObject({
    id: z.string(),
    name: z.string(),
    statusCategory: JiraStatusCategorySchema.optional(),
});
export const JiraIssueTypeSchema = z.looseObject({
    id: z.string(),
    name: z.string(),
    iconUrl: z.string().optional(),
    subtask: z.boolean().optional(),
    hierarchyLevel: z.number().optional(),
});
export const JiraPrioritySchema = z.looseObject({
    id: z.string(),
    name: z.string(),
    iconUrl: z.string().optional(),
});
export const JiraProjectSchema = z.looseObject({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    projectTypeKey: z.string().optional(),
    simplified: z.boolean().optional(),
    style: z.string().optional(),
    avatarUrls: JiraAvatarUrlsSchema.optional(),
    isPrivate: z.boolean().optional(),
});
export const JiraProjectSearchResponseSchema = z.looseObject({
    values: z.array(JiraProjectSchema),
    total: z.number().optional(),
    startAt: z.number().optional(),
    maxResults: z.number().optional(),
    isLast: z.boolean().optional(),
    nextPage: z.string().optional(),
});
/**
 * Вложение задачи. `content` и `thumbnail` — адреса на сайте Jira, доступные только под
 * авторизацией, поэтому наружу они не отдаются: файл выдаёт сервер Jisy под токеном
 * пользователя.
 */
export const JiraAttachmentSchema = z.looseObject({
    id: z.string(),
    filename: z.string().optional(),
    author: z.union([JiraUserSchema, z.null()]).optional(),
    created: z.string().optional(),
    size: z.number().optional(),
    mimeType: z.string().optional(),
    content: z.string().optional(),
    /** Есть только у изображений — Jira рисует миниатюру сама. */
    thumbnail: z.string().optional(),
});
/** Ответ POST /rest/api/3/issue/{key}/attachments — список принятых файлов. */
export const JiraAttachmentsResponseSchema = z.array(JiraAttachmentSchema);
// ── Комментарии ──────────────────────────────────────────────────────────────
export const JiraCommentSchema = z.looseObject({
    id: z.string(),
    author: z.union([JiraUserSchema, z.null()]).optional(),
    updateAuthor: z.union([JiraUserSchema, z.null()]).optional(),
    body: z.union([AdfDocumentSchema, z.null()]).optional(),
    renderedBody: z.string().optional(),
    created: z.string().optional(),
    updated: z.string().optional(),
});
export const JiraCommentsResponseSchema = z.looseObject({
    comments: z.array(JiraCommentSchema),
    total: z.number().optional(),
    startAt: z.number().optional(),
    maxResults: z.number().optional(),
});
// ── Задачи ───────────────────────────────────────────────────────────────────
/**
 * Краткая ссылка на соседнюю задачу: так Jira отдаёт родителя и подзадачи. Поля внутри —
 * урезанный набор (название, статус, приоритет, тип), исполнителя среди них нет.
 */
export const JiraIssueRefSchema = z.looseObject({
    id: z.string(),
    key: z.string(),
    fields: z
        .looseObject({
        summary: z.string().optional(),
        status: JiraStatusSchema.optional(),
        priority: z.union([JiraPrioritySchema, z.null()]).optional(),
        issuetype: JiraIssueTypeSchema.optional(),
    })
        .optional(),
});
/**
 * Поля задачи, которые мы запрашиваем явным списком в POST /rest/api/3/search/jql.
 * Все поля необязательны: набор зависит от параметра `fields` конкретного запроса.
 * Кастомные поля (процент выполнения, дата начала) приходят ключами customfield_*,
 * поэтому объект loose и читается по идентификатору из настроек тенанта.
 */
export const JiraIssueFieldsSchema = z.looseObject({
    summary: z.string().optional(),
    description: z.union([AdfDocumentSchema, z.null()]).optional(),
    issuetype: JiraIssueTypeSchema.optional(),
    status: JiraStatusSchema.optional(),
    priority: z.union([JiraPrioritySchema, z.null()]).optional(),
    assignee: z.union([JiraUserSchema, z.null()]).optional(),
    reporter: z.union([JiraUserSchema, z.null()]).optional(),
    project: JiraProjectSchema.optional(),
    /** ISO-дата без времени. */
    duedate: z.union([z.string(), z.null()]).optional(),
    created: z.string().optional(),
    /** Момент последнего изменения — основа оптимистичной блокировки. */
    updated: z.string().optional(),
    labels: z.array(z.string()).optional(),
    /** Родитель: у подзадачи — задача, у задачи — эпик. */
    parent: JiraIssueRefSchema.optional(),
    /**
     * Подзадачи в том порядке, в каком их показывает сама Jira. Поле читается напрямую, а не
     * поиском, поэтому только что созданная подзадача в нём уже есть. Нужно и перед удалением:
     * Jira уносит подзадачи вместе с родителем.
     */
    subtasks: z.array(JiraIssueRefSchema).optional(),
    attachment: z.array(JiraAttachmentSchema).optional(),
    /**
     * Комментарии приходят прямо в задаче, если запросить поле `comment`: так уведомления
     * собираются одним поиском, а не запросом на каждую задачу. Порция ограничена — сколько
     * всего комментариев, видно по `total`.
     */
    comment: JiraCommentsResponseSchema.optional(),
});
export const JiraRenderedFieldsSchema = z.looseObject({
    /** Готовый HTML описания: expand=renderedFields, используется только для чтения. */
    description: z.union([z.string(), z.null()]).optional(),
});
export const JiraIssueSchema = z.looseObject({
    id: z.string(),
    key: z.string(),
    self: z.string().optional(),
    fields: JiraIssueFieldsSchema,
    renderedFields: JiraRenderedFieldsSchema.optional(),
});
/**
 * Ответ POST /rest/api/3/search/jql.
 * Пагинация курсорная: startAt здесь нет, следующая страница берётся по nextPageToken.
 */
export const JiraSearchResponseSchema = z.looseObject({
    issues: z.array(JiraIssueSchema),
    nextPageToken: z.string().optional(),
    isLast: z.boolean().optional(),
});
export const JiraSearchApproximateCountSchema = z.looseObject({
    count: z.number(),
});
// ── Переходы и поля ──────────────────────────────────────────────────────────
export const JiraTransitionSchema = z.looseObject({
    id: z.string(),
    name: z.string(),
    to: JiraStatusSchema.optional(),
    hasScreen: z.boolean().optional(),
    isAvailable: z.boolean().optional(),
    isConditional: z.boolean().optional(),
});
export const JiraTransitionsResponseSchema = z.looseObject({
    transitions: z.array(JiraTransitionSchema),
});
export const JiraFieldSchemaSchema = z.looseObject({
    /** number, date, datetime, string, … — по нему отбираются кандидаты в настройках. */
    type: z.string().optional(),
    custom: z.string().optional(),
    customId: z.number().optional(),
    system: z.string().optional(),
});
export const JiraFieldSchema = z.looseObject({
    id: z.string(),
    key: z.string().optional(),
    name: z.string(),
    custom: z.boolean().optional(),
    navigable: z.boolean().optional(),
    searchable: z.boolean().optional(),
    clauseNames: z.array(z.string()).optional(),
    schema: JiraFieldSchemaSchema.optional(),
});
export const JiraFieldsResponseSchema = z.array(JiraFieldSchema);
/** Тело ошибки Jira. Приходит и при 400, и при 403, и при 404. */
export const JiraErrorResponseSchema = z.looseObject({
    errorMessages: z.array(z.string()).optional(),
    errors: z.record(z.string(), z.string()).optional(),
    message: z.string().optional(),
});
/**
 * Поля, запрашиваемые для таблицы задач. Запрашивать всё подряд нельзя:
 * лишние поля раздувают ответ и приближают лимиты Jira.
 *
 * Родитель нужен таблице, чтобы вложить подзадачу под её задачу. Список подзадач сюда
 * не входит: счётчик у родителя считается по строкам самих подзадач в кэше — список внутри
 * родителя устаревал бы, ведь смена статуса подзадачи не трогает `updated` родителя.
 */
export const ISSUE_TABLE_FIELDS = [
    'summary',
    'issuetype',
    'status',
    'assignee',
    'priority',
    'duedate',
    'updated',
    'project',
    'parent',
];
/** Поля для модального окна задачи — к табличным добавляются описание и автор. */
export const ISSUE_DETAIL_FIELDS = [
    ...ISSUE_TABLE_FIELDS,
    'description',
    'reporter',
    'created',
    'labels',
    'attachment',
];
/**
 * Поля для уведомлений: кто написал, к чему и когда. Комментарии запрашиваются вместе с
 * задачами — отдельный запрос на каждую задачу упёрся бы в лимиты Jira на первом же десятке.
 */
export const ISSUE_NOTIFICATION_FIELDS = [
    'summary',
    'project',
    'created',
    'assignee',
    'reporter',
    'comment',
];
/** Пользователи, которых можно назначить исполнителем конкретной задачи. */
export const JiraAssignableUsersSchema = z.array(JiraUserSchema);
/** Типы задач, доступные в проекте: ответ /rest/api/3/issue/createmeta/{project}/issuetypes. */
export const JiraCreateMetaIssueTypesSchema = z.looseObject({
    issueTypes: z.array(JiraIssueTypeSchema),
    total: z.number().optional(),
});
/**
 * Поля экрана создания для одного типа задачи:
 * ответ /rest/api/3/issue/createmeta/{project}/issuetypes/{issueTypeId}.
 *
 * `hasDefaultValue` важен не меньше `required`: обязательное поле со значением по умолчанию
 * Jira заполнит сама, и спрашивать его у пользователя не нужно.
 */
export const JiraCreateMetaFieldSchema = z.looseObject({
    fieldId: z.string(),
    name: z.string().optional(),
    required: z.boolean().optional(),
    hasDefaultValue: z.boolean().optional(),
    schema: JiraFieldSchemaSchema.optional(),
});
export const JiraCreateMetaFieldsSchema = z.looseObject({
    fields: z.array(JiraCreateMetaFieldSchema),
    total: z.number().optional(),
});
/**
 * Ответ на создание проекта. Идентификатор здесь приходит числом, а не строкой, как во всех
 * остальных ответах Jira, — поэтому принимаем оба вида и приводим к строке сами.
 */
export const JiraCreatedProjectSchema = z.looseObject({
    id: z.union([z.string(), z.number()]),
    key: z.string(),
    self: z.string().optional(),
});
/**
 * Постановка массовой операции (POST /rest/api/3/bulk/issues/move): Jira выполняет её фоном
 * и отдаёт номер задания, по которому потом спрашивается ход.
 */
export const JiraBulkTaskSchema = z.looseObject({
    taskId: z.union([z.string(), z.number()]),
});
/**
 * Ход массовой операции: GET /rest/api/3/bulk/queue/{taskId}. Статус — ENQUEUED, RUNNING,
 * COMPLETE, FAILED, CANCEL_REQUESTED, CANCELLED или DEAD; разбирается строкой, чтобы новый
 * статус Jira не ронял ответ. Отказы по задачам лежат в `failedAccessibleIssues` под их id.
 */
export const JiraBulkProgressSchema = z.looseObject({
    status: z.string(),
    progressPercent: z.number().optional(),
    processedAccessibleIssues: z.array(z.union([z.number(), z.string()])).optional(),
    failedAccessibleIssues: z.record(z.string(), z.array(z.string())).optional(),
    invalidOrInaccessibleIssueCount: z.number().optional(),
    totalIssueCount: z.number().optional(),
});
/** Ответ на создание задачи: Jira возвращает только идентификаторы. */
export const JiraCreatedIssueSchema = z.looseObject({
    id: z.string(),
    key: z.string(),
    self: z.string().optional(),
});
//# sourceMappingURL=jira.js.map